import React, { useState, useEffect, useMemo } from 'react'
import { Select, Icon, Button, BUTTON_THEME } from '@st-operations/uikit/src/v2'
import { getInsightsEvents } from '_services/analytics'

import { DROPDOWN_MENU_PROPS } from './constants'

const formatTimestamp = (ts) => {
  if (!ts) return ''
  const d = new Date(ts)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()
  const hours = d.getHours()
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const displayHours = String(hours % 12 || 12).padStart(2, '0')
  return `${month}.${day}.${year} - ${displayHours}:${minutes} ${ampm}`
}

// Maps the raw user_events event_name to the user-facing "Download Type" label.
const DOWNLOAD_TYPE_LABELS = {
  'insights-png-download': 'PNG',
  'insights-csv-download': 'CSV',
  'insights-pptx-download': 'PPTX',
  custom_insights_viewed: 'Viewed',
}

const formatDownloadType = (eventName) => DOWNLOAD_TYPE_LABELS[eventName] || eventName || ''

const SORT_FIELD = {
  USER: 'userEmail',
  ORG: 'organizationName',
  AUDIENCE: 'audienceName',
  DOWNLOAD_TYPE: 'downloadType',
  CREATED_AT: 'createdAt',
}

const InsightsTable = ({ orgOptions, source, showInternalUsers }) => {
  const [allData, setAllData] = useState([])
  // Single state object so org + user changes are atomic (avoids double-fetch)
  const [filters, setFilters] = useState({ org: undefined, user: undefined })
  const [downloadTypeFilter, setDownloadTypeFilter] = useState(undefined)
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState(SORT_FIELD.CREATED_AT)
  const [sortAsc, setSortAsc] = useState(false)

  useEffect(() => {
    const params = { source }
    if (filters.org?.value) {
      params.organization_id = filters.org.value
    }
    if (!showInternalUsers) {
      params.exclude_internal = true
    }

    getInsightsEvents(params)
      .then((res) => {
        const items = res?.items || (Array.isArray(res) ? res : [])
        setAllData(
          items.map((item) => ({
            ...item,
            downloadType: formatDownloadType(item.eventName),
          }))
        )
      })
      .catch(() => setAllData([]))
  }, [source, filters.org, showInternalUsers])

  const userOptions = useMemo(() => {
    const uniqueEmails = new Set()
    return allData
      .filter((u) => u.userEmail && !uniqueEmails.has(u.userEmail) && uniqueEmails.add(u.userEmail))
      .map((u) => ({ label: u.userEmail, value: u.userEmail }))
      .sort((a, b) => (a.label > b.label ? 1 : -1))
  }, [allData])

  const downloadTypeOptions = useMemo(() => {
    const uniqueTypes = new Set()
    return allData
      .filter(
        (u) => u.downloadType && !uniqueTypes.has(u.downloadType) && uniqueTypes.add(u.downloadType)
      )
      .map((u) => ({ label: u.downloadType, value: u.downloadType }))
      .sort((a, b) => (a.label > b.label ? 1 : -1))
  }, [allData])

  const filteredData = useMemo(() => {
    let result = allData
    if (filters.user) {
      result = result.filter((u) => u.userEmail === filters.user)
    }
    if (downloadTypeFilter) {
      result = result.filter((u) => u.downloadType === downloadTypeFilter)
    }
    const lowerSearch = search.toLowerCase()
    result = lowerSearch
      ? result.filter(
          (u) =>
            (u.userEmail || '').toLowerCase().includes(lowerSearch) ||
            (u.organizationName || '').toLowerCase().includes(lowerSearch) ||
            (u.audienceName || '').toLowerCase().includes(lowerSearch) ||
            (u.downloadType || '').toLowerCase().includes(lowerSearch) ||
            formatTimestamp(u.createdAt).toLowerCase().includes(lowerSearch)
        )
      : result

    return [...result].sort((a, b) => {
      let aVal = a[sortField] ?? ''
      let bVal = b[sortField] ?? ''
      if (sortField === SORT_FIELD.CREATED_AT) {
        aVal = aVal ? new Date(aVal).getTime() : 0
        bVal = bVal ? new Date(bVal).getTime() : 0
      } else {
        aVal = String(aVal).toLowerCase()
        bVal = String(bVal).toLowerCase()
      }
      if (aVal < bVal) return sortAsc ? -1 : 1
      if (aVal > bVal) return sortAsc ? 1 : -1
      return 0
    })
  }, [allData, filters.user, downloadTypeFilter, search, sortField, sortAsc])

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev)
    } else {
      setSortField(field)
      setSortAsc(false)
    }
  }

  const handleExportCSV = () => {
    if (!filteredData.length) return
    const header = ['User', 'Organization', 'Audience Name', 'Download Type', 'Timestamp']
    const rows = filteredData.map((u) => [
      JSON.stringify(u.userEmail || ''),
      JSON.stringify(u.organizationName || ''),
      JSON.stringify(u.audienceName || ''),
      JSON.stringify(u.downloadType || ''),
      JSON.stringify(formatTimestamp(u.createdAt)),
    ])
    const csv = [header.join(','), ...rows.map((r) => r.join(','))].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.setAttribute('href', URL.createObjectURL(blob))
    link.setAttribute('download', `insights-${new Date().toISOString().split('T')[0]}.csv`)
    link.click()
  }

  const SortIndicator = ({ field }) => (
    <span className={`ml-1 ${sortField === field ? 'text-gray-800' : 'text-gray-400'}`}>
      {sortField === field && sortAsc ? '↑' : '↓'}
    </span>
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row gap-4 flex-wrap items-end">
        <Select
          className="m-0 w-56"
          label="Filter By Organization"
          placeholder="Select organization"
          options={[{ label: 'All', value: undefined }, ...(orgOptions || [])]}
          value={(orgOptions || []).find((o) => o.value === filters.org?.value) || null}
          onChange={(option) =>
            setFilters({ org: option?.value ? option : undefined, user: undefined })
          }
          {...DROPDOWN_MENU_PROPS}
          useReactSelect
          isClearable
        />
        <Select
          className="m-0 w-56"
          label="Filter By User"
          placeholder="Select user"
          options={[{ label: 'All', value: undefined }, ...userOptions]}
          value={userOptions.find((o) => o.value === filters.user) || null}
          onChange={(option) =>
            setFilters((prev) => ({ ...prev, user: option?.value || undefined }))
          }
          {...DROPDOWN_MENU_PROPS}
          useReactSelect
          isClearable
        />
        <Select
          className="m-0 w-56"
          label="Filter By Download Type"
          placeholder="Select download type"
          options={[{ label: 'All', value: undefined }, ...downloadTypeOptions]}
          value={downloadTypeOptions.find((o) => o.value === downloadTypeFilter) || null}
          onChange={(option) => setDownloadTypeFilter(option?.value || undefined)}
          {...DROPDOWN_MENU_PROPS}
          useReactSelect
          isClearable
        />
        <div className="ml-auto">
          <Button
            theme={BUTTON_THEME.PRIMARY}
            onClick={handleExportCSV}
            disabled={!filteredData.length}
          >
            <span className="inline-flex items-center gap-2">
              <Icon
                width="13px"
                height="13px"
                icon="download"
                type="outline"
                color="currentColor"
              />
              Export CSV
            </span>
          </Button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
          </span>
          <input
            type="text"
            placeholder="User, Organization, Audience or Timestamp"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-[4px] text-sm focus:outline-none focus:ring-1 focus:ring-green-600"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-300">
              <th
                className="text-left py-2 pr-4 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                onClick={() => handleSort(SORT_FIELD.USER)}
              >
                User Name <SortIndicator field={SORT_FIELD.USER} />
              </th>
              <th
                className="text-left py-2 pr-4 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                onClick={() => handleSort(SORT_FIELD.ORG)}
              >
                Organization <SortIndicator field={SORT_FIELD.ORG} />
              </th>
              <th
                className="text-left py-2 pr-4 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                onClick={() => handleSort(SORT_FIELD.AUDIENCE)}
              >
                Audience Name <SortIndicator field={SORT_FIELD.AUDIENCE} />
              </th>
              <th
                className="text-left py-2 pr-4 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                onClick={() => handleSort(SORT_FIELD.DOWNLOAD_TYPE)}
              >
                Download Type <SortIndicator field={SORT_FIELD.DOWNLOAD_TYPE} />
              </th>
              <th
                className="text-left py-2 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap border-b-2 border-green-700"
                onClick={() => handleSort(SORT_FIELD.CREATED_AT)}
              >
                Timestamp <SortIndicator field={SORT_FIELD.CREATED_AT} />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-6 text-center text-gray-400">
                  No data
                </td>
              </tr>
            ) : (
              filteredData.map((u, i) => (
                <tr key={(u.userEmail || '') + i} className="border-b border-gray-200">
                  <td className="py-3 pr-4 font-semibold text-gray-700">{u.userEmail}</td>
                  <td className="py-3 pr-4 text-gray-700">{u.organizationName}</td>
                  <td className="py-3 pr-4 text-gray-700">{u.audienceName}</td>
                  <td className="py-3 pr-4 text-gray-700">{u.downloadType}</td>
                  <td className="py-3 text-gray-700">{formatTimestamp(u.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default InsightsTable
